import type { RouteLocationNormalized } from 'vue-router'
import type { AnalyticsEvent } from '@core/analytics/types'
import { Provider, type ProviderParams } from '@core/analytics/objects/Provider'
import { CurrencyPair, type CurrencyPairParams } from '@core/analytics/objects/CurrencyPair'
import { Corridor, type CorridorParams } from '@core/analytics/objects/Corridor'
import { Country, type CountryParams } from '@core/analytics/objects/Country'
import {
  PageView,
  HomeView,
  ErrorView,
  LandingView,
  MoneyTransferView,
  CountryView,
  CurrencyView,
  ReviewView,
  AppPageView,
} from '@core/analytics/core-events/page'
import { PageTypes } from '@core/constants/pages'

export const ROUTER_ORIGIN = 'router'

export type AnalyticsParams = (
  | { type: PageTypes.COUNTRY; country: CountryParams }
  | { type: PageTypes.CURRENCY; currencies: CurrencyPairParams }
  | { type: PageTypes.REVIEW; psp: ProviderParams }
  | { type: PageTypes.TRANSFER; corridor: CorridorParams }
  | { type: PageTypes.HOME }
  | { type: PageTypes.LANDING }
  | { type: PageTypes.APP }
) & { page: { tags: string[] } } & {
  page: {
    context: {
      countryFrom?: string | null
      countryTo?: string | null
      provider?: string | null
      payout?: string | null
      vertical?: string | null
    }
  }
}

function createEvent(
  route: RouteLocationNormalized,
  params: AnalyticsParams | undefined,
  slices: string[],
  pageContext: AnalyticsParams['page']['context'] | undefined,
  tags: string[] | undefined,
) {
  if (!params?.type) {
    return PageView(String(route.name), { path: route.path, tags })
  }

  switch (params.type) {
    case PageTypes.COUNTRY:
      return CountryView(Country.getInstance(params.country), { slices, pageContext, tags })
    case PageTypes.REVIEW:
      return ReviewView(new Provider(params.psp), { slices, pageContext, tags })
    case PageTypes.TRANSFER:
      return MoneyTransferView(Corridor.getInstance(params.corridor), { slices, pageContext, tags })
    case PageTypes.CURRENCY:
      return CurrencyView(CurrencyPair.getInstance(params.currencies), {
        slices,
        pageContext,
        tags,
      })
    case PageTypes.LANDING:
      return LandingView({ slices, pageContext, tags })
    case PageTypes.HOME:
      return HomeView({ slices, pageContext, tags })
    case PageTypes.APP:
      return AppPageView(String(route.name), { pageContext, tags })
    default:
      return PageView(String(route.name), { path: route.path, tags })
  }
}

export function getPageView(
  route: RouteLocationNormalized,
  params?: AnalyticsParams,
): AnalyticsEvent {
  if (window.__NUXT__?.error) {
    return ErrorView(window.__NUXT__.error)
  }

  const pageContext = params?.page.context
  const tags = params?.page.tags
  const slices: string[] = [...document.querySelectorAll('[data-widget-type]')]
    .filter((s) => (s as HTMLElement).dataset.widgetType !== undefined)
    .map((s) => (s as HTMLElement).dataset.widgetType as string)

  const event = createEvent(route, params, slices, pageContext, tags)
  return { ...event, origin: ROUTER_ORIGIN }
}
